// Admin Dashboard Overview Page
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import LoadingSpinner from '../../components/admin/LoadingSpinner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Label,
  ComposedChart, Area, Line,
} from 'recharts';

interface DashboardStats {
  totalChildren: number;
  teamMembers: number;
  totalStaff: number;
  volunteers: number;
  monthlyDonations: number;
  totalDonations: number;
  totalMessages: number;
  pendingRequests: number;
  unreadMessages: number;
  galleryImages: number;
  testimonials: number;
}

interface MonthlyData {
  month: string;
  messages: number;
  donations: number;
  volunteers: number;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalChildren: 0,
    teamMembers: 0,
    totalStaff: 0,
    volunteers: 0,
    monthlyDonations: 0,
    totalDonations: 0,
    totalMessages: 0,
    pendingRequests: 0,
    unreadMessages: 0,
    galleryImages: 0,
    testimonials: 0,
  });
  const [loading, setLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [
        childrenRes,
        teamRes,
        volunteersRes,
        donationsRes,
        volunteerAppsRes,
        messagesRes,
        allMessagesRes,
        galleryRes,
        testimonialsRes,
      ] = await Promise.all([
        fetch('http://localhost:5000/api/children'),
        fetch('http://localhost:5000/api/team'),
        fetch('http://localhost:5000/api/volunteers'),
        fetch('http://localhost:5000/api/donations'),
        fetch('http://localhost:5000/api/volunteers/applications?status=pending'),
        fetch('http://localhost:5000/api/contact?status=unread'),
        fetch('http://localhost:5000/api/contact'),
        fetch('http://localhost:5000/api/gallery'),
        fetch('http://localhost:5000/api/testimonials'),
      ]);

      const childrenData      = await childrenRes.json();
      const teamData          = await teamRes.json();
      const volunteersData    = await volunteersRes.json();
      const donationsData     = await donationsRes.json();
      const volunteerAppsData = await volunteerAppsRes.json();
      const messagesData      = await messagesRes.json();
      const allMessagesData   = await allMessagesRes.json();
      const galleryData       = await galleryRes.json();
      const testimonialsData  = await testimonialsRes.json();

      const children      = Array.isArray(childrenData) ? childrenData : [];
      const team          = Array.isArray(teamData) ? teamData : [];
      const unreadMsgs    = messagesData.messages || [];
      const allMessages   = allMessagesData.messages || [];
      const donations     = donationsData.donations || (Array.isArray(donationsData) ? donationsData : []);
      const volunteerApps = volunteerAppsData.applications || (Array.isArray(volunteerAppsData) ? volunteerAppsData : []);
      const gallery       = Array.isArray(galleryData) ? galleryData : [];
      const testimonials  = Array.isArray(testimonialsData) ? testimonialsData : [];

      const totalDonations = donations.reduce((sum: number, d: any) => sum + parseFloat(d.amount || 0), 0);
      const now = new Date();
      const monthlyTotal = donations
        .filter((d: any) => { const dd = new Date(d.created_at); return dd.getMonth() === now.getMonth() && dd.getFullYear() === now.getFullYear(); })
        .reduce((sum: number, d: any) => sum + parseFloat(d.amount || 0), 0);

      setStats({
        totalChildren: children.length,
        teamMembers: team.length,
        totalStaff: team.filter((t: any) => {
          const r = (t.role || '').toLowerCase();
          return r.includes('staff') || r.includes('director') || r.includes('coordinator');
        }).length,
        volunteers: Array.isArray(volunteersData) ? volunteersData.length : 0,
        monthlyDonations: monthlyTotal,
        totalDonations,
        totalMessages: allMessages.length,
        pendingRequests: volunteerApps.length,
        unreadMessages: unreadMsgs.length,
        galleryImages: gallery.length,
        testimonials: testimonials.length,
      });

      // Monthly analytics — last 6 months
      const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const monthlyAnalytics: MonthlyData[] = [];
      for (let i = 5; i >= 0; i--) {
        const t = new Date();
        t.setDate(1);
        t.setMonth(t.getMonth() - i);
        const shortYear = t.getFullYear() !== now.getFullYear() ? ` '${t.getFullYear().toString().slice(-2)}` : '';
        monthlyAnalytics.push({
          month: `${MONTHS[t.getMonth()]}${shortYear}`,
          messages: allMessages.filter((m: any) => { const d = new Date(m.created_at); return d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear(); }).length,
          donations: donations.filter((d: any) => { const dd = new Date(d.created_at); return dd.getMonth() === t.getMonth() && dd.getFullYear() === t.getFullYear(); }).reduce((s: number, d: any) => s + parseFloat(d.amount || 0), 0),
          volunteers: volunteerApps.filter((v: any) => { const d = new Date(v.created_at); return d.getMonth() === t.getMonth() && d.getFullYear() === t.getFullYear(); }).length,
        });
      }
      setMonthlyData(monthlyAnalytics);

      // Recent activities
      const allActivities = [
        ...gallery.map((g: any)       => ({ type: 'gallery',     message: 'New photo uploaded in Gallery',       detail: g.title || 'Untitled',       time: new Date(g.created_at || g.updated_at),  icon: '🖼️', color: '#8B5CF6' })),
        ...team.map((t: any)          => ({ type: 'team',        message: 'New team member added',               detail: t.name,                      time: new Date(t.created_at || t.updated_at),  icon: '👤', color: '#10B981' })),
        ...children.map((c: any)      => ({ type: 'children',    message: 'New child profile added',             detail: c.name,                      time: new Date(c.created_at || c.updated_at),  icon: '👶', color: '#03A84A' })),
        ...volunteerApps.map((v: any) => ({ type: 'volunteer',   message: `${v.full_name} applied as volunteer`, detail: v.phone || v.email,          time: new Date(v.created_at),                  icon: '🙋', color: '#F59E0B' })),
        ...allMessages.map((m: any)   => ({ type: 'message',     message: `New message from ${m.full_name}`,     detail: m.subject,                   time: new Date(m.created_at),                  icon: '✉️', color: '#3B82F6' })),
        ...testimonials.map((t: any)  => ({ type: 'testimonial', message: 'New testimonial received',            detail: t.name,                      time: new Date(t.created_at || t.updated_at),  icon: '⭐', color: '#EC4899' })),
        ...donations.map((d: any)     => ({ type: 'donation',    message: 'New donation received',               detail: `NPR ${d.amount}`,           time: new Date(d.created_at),                  icon: '💵', color: '#03A84A' })),
      ]
        .filter((a: any) => !isNaN(a.time.getTime()))
        .sort((a, b) => b.time.getTime() - a.time.getTime())
        .slice(0, 8);
      setRecentActivities(allActivities);
      setLoading(false);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setLoading(false);
    }
  };

  const getTimeAgo = (date: Date) => {
    const diff = Date.now() - date.getTime();
    const mins = Math.floor(diff / 60000);
    const hrs  = Math.floor(mins / 60);
    const days = Math.floor(hrs / 24);
    if (mins  <  1) return 'Just now';
    if (mins  < 60) return `${mins}m ago`;
    if (hrs   < 24) return `${hrs}h ago`;
    return `${days}d ago`;
  };

  if (loading) {
    return <AdminLayout><LoadingSpinner fullScreen /></AdminLayout>;
  }

  // ── Style tokens ──────────────────────────────────────────────────
  const card: React.CSSProperties = {
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 2px 16px rgba(12,34,57,0.055)',
    border: '1px solid rgba(12,34,57,0.05)',
  };
  const sLabel: React.CSSProperties = {
    margin: 0, color: '#94A3B8', fontSize: '11px', fontWeight: '700',
    textTransform: 'uppercase', letterSpacing: '0.09em',
  };

  // ── Chart data ────────────────────────────────────────────────────
  const barData = monthlyData.map((d, idx) => ({
    month: d.month,
    'This Period': d.donations,
    'Last Period': idx > 0 ? monthlyData[idx - 1].donations : 0,
  }));

  const donutData = [
    { name: 'Children',   value: stats.totalChildren,  color: '#0C2239' },
    { name: 'Team',       value: stats.teamMembers,    color: '#03A84A' },
    { name: 'Volunteers', value: stats.volunteers,     color: '#1557B0' },
    { name: 'Gallery',    value: stats.galleryImages,  color: '#F59E0B' },
  ];
  const donutTotal   = donutData.reduce((s, d) => s + d.value, 0);
  const donutDisplay = donutTotal > 0
    ? donutData.filter(d => d.value > 0)
    : [{ name: 'No data', value: 1, color: '#E8EDF3' }];

  const lineData = monthlyData.map((d, idx) => ({
    month: d.month.slice(0, 3),
    'This Period': d.messages,
    'Last Period': idx > 0 ? monthlyData[idx - 1].messages : 0,
  }));

  const quickActions = [
    { label: 'Messages',    icon: '✉️', color: '#1557B0', route: '/admin/messages' },
    { label: 'Add Child',   icon: '🧒', color: '#03A84A', route: '/admin/children' },
    { label: 'New Update',  icon: '📝', color: '#03A84A', route: '/admin/content' },
    { label: 'Gallery',     icon: '🖼️', color: '#8B5CF6', route: '/admin/gallery' },
    { label: 'Team',        icon: '👥', color: '#0C2239', route: '/admin/team' },
    { label: 'Volunteers',  icon: '🙋', color: '#F59E0B', route: '/admin/volunteers' },
  ];

  // ── Custom Tooltips ───────────────────────────────────────────────
  const DonationTooltip = ({ active, payload, label: ttLbl }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: '#0C2239', borderRadius: '10px', padding: '10px 14px', boxShadow: '0 8px 24px rgba(12,34,57,0.30)' }}>
        <p style={{ color: '#94A3B8', fontSize: '11px', fontWeight: '600', margin: '0 0 6px' }}>{ttLbl}</p>
        {payload.map((p: any, i: number) => (
          <p key={i} style={{ color: p.dataKey === 'This Period' ? '#FFFFFF' : '#B8C5D3', fontSize: '13px', fontWeight: '700', margin: '2px 0' }}>
            {p.dataKey}: NPR {Number(p.value).toLocaleString()}
          </p>
        ))}
      </div>
    );
  };

  const MessageTooltip = ({ active, payload, label: ttLbl }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: '#0C2239', borderRadius: '10px', padding: '10px 14px', boxShadow: '0 8px 24px rgba(12,34,57,0.30)' }}>
        <p style={{ color: '#94A3B8', fontSize: '11px', fontWeight: '600', margin: '0 0 6px' }}>{ttLbl}</p>
        {payload.map((p: any, i: number) => (
          <p key={i} style={{ color: p.dataKey === 'This Period' ? '#FFFFFF' : '#B8C5D3', fontSize: '13px', fontWeight: '700', margin: '2px 0' }}>
            {p.dataKey}: {p.value} msgs
          </p>
        ))}
      </div>
    );
  };

  return (
    <AdminLayout>

      {/* ── Page Header ──────────────────────────────────────────── */}
      <div style={{ marginBottom: '26px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ color: '#0C2239', fontSize: '26px', fontWeight: '800', margin: 0, letterSpacing: '-0.03em' }}>
            Dashboard
          </h1>
          <p style={{ color: '#94A3B8', fontSize: '13px', fontWeight: '500', marginTop: '5px', marginBottom: 0 }}>
            Welcome back! Here's what's happening with New Life Nepal today.
          </p>
        </div>
        <div style={{
          padding: '7px 14px', background: '#FFFFFF', borderRadius: '20px',
          border: '1px solid #E8EDF3', fontSize: '12px', color: '#64748B', fontWeight: '600',
          boxShadow: '0 1px 6px rgba(12,34,57,0.04)',
        }}>
          {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </div>
      </div>

      {/* ── Row 1: BarChart + Donut ───────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.65fr 1fr', gap: '20px', marginBottom: '20px' }}>

        {/* Total Donations */}
        <div style={card} className="nln-fade-up">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
            <div>
              <p style={sLabel}>Total Donations</p>
              <h2 style={{ color: '#0C2239', fontSize: '34px', fontWeight: '900', margin: '6px 0 5px', letterSpacing: '-0.04em' }}>
                NPR {stats.totalDonations > 0 ? stats.totalDonations.toLocaleString() : '—'}
              </h2>
              <span className="nln-stat-chip up">↑ Active fundraising</span>
            </div>
            <button className="nln-view-btn" onClick={() => navigate('/admin/donors')}>View Report</button>
          </div>
          <p style={{ color: '#B8C5D3', fontSize: '11.5px', fontWeight: '500', margin: '10px 0 16px' }}>
            {monthlyData[0]?.month} – {monthlyData[monthlyData.length - 1]?.month} &nbsp;·&nbsp; Last 6 months
          </p>

          <ResponsiveContainer width="100%" height={155}>
            <BarChart data={barData} barGap={3} barCategoryGap="40%" margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#F1F5F9" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10.5, fill: '#94A3B8', fontWeight: 600, fontFamily: 'Inter,sans-serif' }}
                axisLine={false} tickLine={false}
              />
              <YAxis hide domain={[0, 'auto']} />
              <Tooltip content={<DonationTooltip />} cursor={{ fill: 'rgba(21,87,176,0.04)' }} />
              <Bar dataKey="Last Period" fill="#E2E8F0" radius={[4, 4, 0, 0]} maxBarSize={16} />
              <Bar dataKey="This Period" fill="#1557B0" radius={[4, 4, 0, 0]} maxBarSize={16} />
            </BarChart>
          </ResponsiveContainer>

          <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
            {[{ color: '#1557B0', label: 'This Period' }, { color: '#E2E8F0', label: 'Last Period' }].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <div style={{ width: '22px', height: '4px', borderRadius: '2px', background: item.color }} />
                <span style={{ fontSize: '11.5px', color: '#64748B', fontWeight: '600' }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Activity Breakdown Donut */}
        <div style={{ ...card, display: 'flex', flexDirection: 'column' }} className="nln-fade-up">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
            <div>
              <p style={sLabel}>Activity Breakdown</p>
              <h3 style={{ color: '#0C2239', fontSize: '17px', fontWeight: '700', margin: '6px 0 0' }}>
                {donutTotal} Total Records
              </h3>
            </div>
            <button className="nln-view-btn" onClick={() => navigate('/admin/children')}>View Report</button>
          </div>
          <p style={{ color: '#B8C5D3', fontSize: '11.5px', fontWeight: '500', margin: '4px 0 6px' }}>
            All-time across categories
          </p>

          <div style={{ flex: 1 }}>
            <ResponsiveContainer width="100%" height={175}>
              <PieChart>
                <Pie
                  data={donutDisplay}
                  cx="50%" cy="50%"
                  innerRadius={58} outerRadius={82}
                  paddingAngle={donutTotal > 0 ? 3 : 0}
                  dataKey="value"
                  startAngle={90} endAngle={-270}
                  strokeWidth={0}
                >
                  {donutDisplay.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                  <Label
                    content={({ viewBox }: any) => {
                      const { cx, cy } = viewBox;
                      return (
                        <g>
                          <text x={cx} y={cy - 6} textAnchor="middle" fill="#0C2239"
                            fontSize={24} fontWeight={800} fontFamily="Inter,sans-serif">
                            {donutTotal}
                          </text>
                          <text x={cx} y={cy + 13} textAnchor="middle" fill="#94A3B8"
                            fontSize={10} fontWeight={600} fontFamily="Inter,sans-serif">
                            TOTAL
                          </text>
                        </g>
                      );
                    }}
                    position="center"
                  />
                </Pie>
                <Tooltip
                  formatter={(value: any) => [value]}
                  contentStyle={{ borderRadius: '10px', border: 'none', background: '#0C2239', boxShadow: '0 8px 24px rgba(12,34,57,0.25)' }}
                  itemStyle={{ color: '#FFFFFF', fontSize: '13px', fontWeight: 700 }}
                  labelStyle={{ color: '#94A3B8', fontSize: '11px', marginBottom: '4px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 6px' }}>
            {donutData.map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '8px', background: '#F8FAFC' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                <div>
                  <p style={{ margin: 0, fontSize: '11px', color: '#64748B', fontWeight: '600' }}>{d.name}</p>
                  <p style={{ margin: 0, fontSize: '13px', color: '#0C2239', fontWeight: '700' }}>{d.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Row 2: Impact + Activity + Messages ───────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.15fr 1fr', gap: '20px' }}>

        {/* Our Impact — circles */}
        <div style={card} className="nln-fade-up">
          <p style={sLabel}>Our Impact</p>
          <p style={{ color: '#94A3B8', fontSize: '12px', fontWeight: '500', margin: '4px 0 18px' }}>At a glance</p>

          <div style={{ position: 'relative', height: '180px' }}>
            {/* Children — navy, top-left */}
            <div style={{
              position: 'absolute', left: '0%', top: '12%',
              width: '108px', height: '108px', borderRadius: '50%',
              background: 'linear-gradient(145deg, #0C2239 0%, #1a4470 100%)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 20px rgba(12,34,57,0.28)', zIndex: 1,
            }}>
              <span style={{ color: '#FFF', fontSize: '26px', fontWeight: '900', lineHeight: 1 }}>{stats.totalChildren}</span>
              <span style={{ color: 'rgba(255,255,255,0.60)', fontSize: '10px', fontWeight: '600', marginTop: '5px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Children</span>
            </div>

            {/* Volunteers — green, top-right */}
            <div style={{
              position: 'absolute', right: '0%', top: '0%',
              width: '126px', height: '126px', borderRadius: '50%',
              background: 'linear-gradient(145deg, #03A84A 0%, #04c957 100%)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 20px rgba(3,168,74,0.32)', zIndex: 2,
            }}>
              <span style={{ color: '#FFF', fontSize: '28px', fontWeight: '900', lineHeight: 1 }}>{stats.volunteers}</span>
              <span style={{ color: 'rgba(255,255,255,0.60)', fontSize: '10px', fontWeight: '600', marginTop: '5px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Volunteers</span>
            </div>

            {/* Team — blue, bottom-center */}
            <div style={{
              position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: '48%',
              width: '96px', height: '96px', borderRadius: '50%',
              background: 'linear-gradient(145deg, #1557B0 0%, #1d6ed8 100%)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 20px rgba(21,87,176,0.28)', zIndex: 3,
            }}>
              <span style={{ color: '#FFF', fontSize: '22px', fontWeight: '900', lineHeight: 1 }}>{stats.teamMembers}</span>
              <span style={{ color: 'rgba(255,255,255,0.60)', fontSize: '10px', fontWeight: '600', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Team</span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginTop: '10px' }}>
            {[
              { label: 'Children in care',  count: stats.totalChildren, color: '#0C2239' },
              { label: 'Active volunteers', count: stats.volunteers,    color: '#03A84A' },
              { label: 'Team members',      count: stats.teamMembers,   color: '#1557B0' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.color }} />
                  <span style={{ fontSize: '12px', color: '#64748B', fontWeight: '500' }}>{item.label}</span>
                </div>
                <span style={{ fontSize: '13px', color: '#0C2239', fontWeight: '700' }}>{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div style={card} className="nln-fade-up">
          <p style={sLabel}>Recent Activity</p>
          <p style={{ color: '#94A3B8', fontSize: '12px', fontWeight: '500', margin: '4px 0 14px' }}>Latest updates across NLN</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {recentActivities.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#94A3B8', fontSize: '13px', padding: '32px 0' }}>No recent activity</p>
            ) : (
              recentActivities.slice(0, 6).map((activity, i) => (
                <div
                  key={i}
                  className="nln-activity-row"
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '9px 8px', borderBottom: i < 5 ? '1px solid #F8FAFC' : 'none' }}
                >
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                    background: activity.color + '14', border: `1px solid ${activity.color}22`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px',
                  }}>
                    {activity.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, color: '#0C2239', fontSize: '12.5px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {activity.message}
                    </p>
                    {activity.detail && (
                      <p style={{ margin: '2px 0 0', color: '#94A3B8', fontSize: '11px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {activity.detail}
                      </p>
                    )}
                  </div>
                  <span style={{ fontSize: '10.5px', color: '#B8C5D3', fontWeight: '600', flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {getTimeAgo(activity.time)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Messages Trend */}
        <div style={card} className="nln-fade-up">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
            <div>
              <p style={sLabel}>Messages</p>
              <h2 style={{ color: '#0C2239', fontSize: '32px', fontWeight: '900', margin: '6px 0 4px', letterSpacing: '-0.04em' }}>
                {stats.totalMessages}
              </h2>
              <span className={`nln-stat-chip ${stats.unreadMessages > 0 ? 'down' : 'up'}`}>
                {stats.unreadMessages > 0 ? `↓ ${stats.unreadMessages} unread` : '↑ All clear'}
              </span>
            </div>
            <button className="nln-view-btn" onClick={() => navigate('/admin/messages')}>View Report</button>
          </div>
          <p style={{ color: '#B8C5D3', fontSize: '11.5px', fontWeight: '500', margin: '10px 0 14px' }}>
            {monthlyData[0]?.month} – {monthlyData[monthlyData.length - 1]?.month}
          </p>

          <ResponsiveContainer width="100%" height={115}>
            <ComposedChart data={lineData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="msgGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#03A84A" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#03A84A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 9.5, fill: '#94A3B8', fontWeight: 600, fontFamily: 'Inter,sans-serif' }} axisLine={false} tickLine={false} />
              <YAxis hide domain={[0, 'auto']} />
              <Tooltip content={<MessageTooltip />} cursor={{ stroke: 'rgba(3,168,74,0.15)', strokeWidth: 1 }} />
              <Area
                type="monotone" dataKey="This Period"
                stroke="#03A84A" strokeWidth={2.5} fill="url(#msgGradient)"
                dot={{ fill: '#FFFFFF', stroke: '#03A84A', strokeWidth: 2.5, r: 4 }}
                activeDot={{ r: 6, fill: '#03A84A', stroke: '#FFFFFF', strokeWidth: 2 }}
              />
              <Line
                type="monotone" dataKey="Last Period"
                stroke="#CBD5E1" strokeWidth={2} strokeDasharray="4 3" dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>

          <div style={{ display: 'flex', gap: '16px', marginTop: '10px' }}>
            {[
              { color: '#03A84A', label: 'This Period', dashed: false },
              { color: '#CBD5E1', label: 'Last Period', dashed: true },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {item.dashed
                  ? <div style={{ width: '18px', height: 0, borderTop: `2px dashed ${item.color}` }} />
                  : <div style={{ width: '18px', height: '3px', borderRadius: '2px', background: item.color }} />
                }
                <span style={{ fontSize: '11px', color: '#64748B', fontWeight: '600' }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Row 3: KPI Chips ──────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '20px' }}>
        {[
          { label: 'Gallery Images',   value: stats.galleryImages,   color: '#8B5CF6', icon: '🖼️', route: '/admin/gallery' },
          { label: 'Testimonials',     value: stats.testimonials,    color: '#F59E0B', icon: '⭐', route: '/admin/content' },
          { label: 'Unread Messages',  value: stats.unreadMessages,  color: '#EF4444', icon: '✉️', route: '/admin/messages' },
          { label: 'Pending Requests', value: stats.pendingRequests, color: '#1557B0', icon: '⏳', route: '/admin/volunteers' },
        ].map((item, i) => (
          <div
            key={i}
            className="nln-fade-up"
            onClick={() => navigate(item.route)}
            style={{ ...card, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px', padding: '18px 20px', animationDelay: `${i * 60}ms` }}
          >
            <div style={{
              width: '46px', height: '46px', borderRadius: '12px', flexShrink: 0,
              background: item.color + '14', border: `1px solid ${item.color}22`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px',
            }}>
              {item.icon}
            </div>
            <div>
              <p style={{ ...sLabel, marginBottom: '4px' }}>{item.label}</p>
              <h3 style={{ margin: 0, color: '#0C2239', fontSize: '24px', fontWeight: '800', letterSpacing: '-0.03em' }}>
                {item.value}
              </h3>
            </div>
          </div>
        ))}
      </div>

      {/* ── Row 4: Quick Actions ──────────────────────────────────── */}
      <div style={{ ...card, marginTop: '20px' }} className="nln-fade-up">
        <div style={{ marginBottom: '18px' }}>
          <p style={sLabel}>Quick Actions</p>
          <p style={{ color: '#94A3B8', fontSize: '12px', fontWeight: '500', margin: '4px 0 0' }}>Common shortcuts</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px' }}>
          {quickActions.map((action, i) => (
            <button
              key={i}
              className="nln-quick-btn"
              onClick={() => navigate(action.route)}
              style={{ background: action.color + '0c', border: `1.5px solid ${action.color}20` }}
              onMouseEnter={e => { e.currentTarget.style.background = action.color + '18'; e.currentTarget.style.borderColor = action.color + '45'; }}
              onMouseLeave={e => { e.currentTarget.style.background = action.color + '0c'; e.currentTarget.style.borderColor = action.color + '20'; }}
            >
              <div style={{
                width: '42px', height: '42px', borderRadius: '12px', background: '#FFFFFF',
                border: `1px solid ${action.color}18`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: '20px', boxShadow: `0 2px 8px ${action.color}18`,
              }}>
                {action.icon}
              </div>
              <span style={{ color: action.color, fontSize: '11.5px', fontWeight: '700', textAlign: 'center', lineHeight: 1.2 }}>
                {action.label}
              </span>
            </button>
          ))}
        </div>
      </div>

    </AdminLayout>
  );
};

export default AdminDashboard;
